import { html, type TemplateResult } from 'lit-html'
import { chartedPercent, SHARE_CARD_UI_KEYS, shareCardLines, type ShareCard } from '@guild/core'

import type { App } from './app.ts'

/**
 * The chart card on a screen (Q32.1): the island's full 20 x 12 thumbnail --
 * the same glyph grid the Atlas draws, since the screen has the fonts -- over
 * the sentences, and `[ copy the card ]`, which copies the text form. What you
 * see is the fine chart; what you paste is the same chart in squares any chat
 * can show.
 *
 * One template, because the card is drawn in two places: the day's end, and
 * the notice from the Keeper of the Rolls in the post the morning an island
 * goes under (Q32.2). The sentences are the card's own, resolved from the same
 * keys the server renders the text form from; the text itself is `share.txt`,
 * already rendered in the reader's locale, and is what the verb copies.
 */

export type ChartCardProps = {
  readonly card: ShareCard
  /** `share.txt`, or null while it is still on its way. */
  readonly text: string | null
  /** The Atlas thumbnail of the island, or null when the Atlas has none. */
  readonly thumbnail: string | null
  readonly copied: boolean
  readonly onCopied: () => void
}

export function chartCard(app: App, props: ChartCardProps): TemplateResult {
  const { card } = props
  const alt = app.t(SHARE_CARD_UI_KEYS.chartAlt, {
    name: card.islandName,
    pct: chartedPercent(card),
  })
  return html`<div class="chart-card">
    ${
      props.thumbnail === null
        ? null
        : html`<div class="thumb thumb-large" role="img" aria-label=${alt}>
            ${[...props.thumbnail].map((glyph) => html`<span>${glyph}</span>`)}
          </div>`
    }
    <ul class="chart-lines">
      ${shareCardLines(card).map((line) => html`<li>${app.t(line.key, line.args)}</li>`)}
    </ul>
    <div class="row">
      <button
        class="verb chart-copy"
        ?disabled=${props.text === null}
        @click=${async () => {
          if (props.text === null) return
          try {
            await navigator.clipboard.writeText(props.text)
            props.onCopied()
          } catch {
            // Some browsers refuse the clipboard outside a trusted gesture;
            // the sentences are on the screen and can still be read out.
          }
        }}
      >
        [ ${app.t(SHARE_CARD_UI_KEYS.copy)} ]
      </button>
      ${props.copied ? html`<span class="quiet-ink">${app.t('copied')}</span>` : null}
    </div>
  </div>`
}
